import React from 'react';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import type { TypographyProps } from '@mui/material';
import { alpha } from '@mui/material/styles';

interface ShimmerTextProps {
  children: string;
  icon?: React.ReactNode;
  variant?: TypographyProps['variant'];
  cycleSeconds?: number;
}

// Shared with SparkleIndicator (web/core) so the text sweep and the
// leading sparkle pulse move at the same speed.
export const SHIMMER_CYCLE_SECONDS = 3.2;

/**
 * Animated text for in-progress / "thinking" states (e.g. AI generation).
 * `icon` renders alongside the text rather than inside the Typography, so
 * the text node stays plain text. Falls back to a static layout when the
 * user prefers reduced motion, so it never introduces motion the
 * OS-level setting asked us not to show.
 */
export const ShimmerText = ({
  children,
  icon = null,
  variant = 'body1',
  cycleSeconds = SHIMMER_CYCLE_SECONDS,
}: ShimmerTextProps) => {
  const theme = useTheme();
  const prefersReducedMotion = useMediaQuery(
    '(prefers-reduced-motion: reduce)'
  );

  if (prefersReducedMotion) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {icon}
        <Typography variant={variant}>{children}</Typography>
      </Box>
    );
  }

  const dim = alpha(theme.palette.text.primary, 0.35);
  const bright = theme.palette.text.primary;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      {icon}
      <Typography
        variant={variant}
        sx={{
          backgroundImage: `linear-gradient(90deg, ${dim} 0%, ${dim} 35%, ${bright} 50%, ${dim} 65%, ${dim} 100%)`,
          backgroundSize: '200% 100%',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          color: 'transparent',
          animation: `pv-shimmer-text ${cycleSeconds}s linear infinite`,
          '@keyframes pv-shimmer-text': {
            '0%': { backgroundPosition: '150% 0' },
            '100%': { backgroundPosition: '-50% 0' },
          },
        }}
      >
        {children}
      </Typography>
    </Box>
  );
};
